import React, { useEffect } from "react";
import Container from "react-bootstrap/Container";
import { getUserRequests } from "../../../slices/users/userManagementSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../slices/store";
import FilterSection from "./FilterSection";
import { useLocation } from "react-router-dom";
import { requestedUserStatusData } from "./constants";
import useRequestPendingUser from "../../../hooks/useRequestPendingUsers";
import Loader from "../../../components/atoms/Loader/Loader";
import UserCard from "../../../components/organisms/Cards/user-card-controller/UserCard";
import SearchHelper from "../../../components/organisms/Cards/user-card-controller/SearchHelper";
import Headline from "../../../components/organisms/Headline/Headline";
import BreadCrumb from "../../../components/organisms/Breadcrumb/BreadCrumb";
import useDebouncedDispatch from "../../../utils/DebouncedDispatch";
import Pagination from "../../../components/molecules/Pagination/Pagination";

const UserRequests = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const status = useSelector((state: RootState) => state.userManagement.status);
  const requestedUsers = useSelector(
    (state: RootState) => state.userManagement.requestedUsers
  );

  const [requestedUserData, setRequestedUserData] =
    React.useState(requestedUsers);
  const [clickFilterStatus, setClickFilterStatus] = React.useState(false);
  const [clickFilterSearch, setClickFilterSearch] = React.useState(false);
  const [paginationChanged, setPaginationChanged] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [reqRequests, updateSettings] = useRequestPendingUser();
  const [activePage, setActivePage] = React.useState(reqRequests.PageNumber);
  const [selectedOption, setSelectedOption] = React.useState<any>({
    value: 1,
    label: "All",
  });

  const debouncedDispatch = useDebouncedDispatch(dispatch);

  useEffect(() => {
    setIsLoading(true);

    if (reqRequests) {
      dispatch(getUserRequests(reqRequests));
    }

    localStorage.setItem("userLocation", "/user/requests");

    const storedOption = JSON.parse(
      localStorage.getItem("selectedOptionRequest") || "null"
    );

    if (storedOption) {
      setSelectedOption(storedOption);
    }

    const activePageInLocal = localStorage.getItem("requestActivePage");

    if (activePageInLocal) {
      const page = parseInt(activePageInLocal, 10);
      setActivePage(page);
      updateSettings({ PageNumber: page });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRequestedUserData(requestedUsers);
    setIsLoading(false);
  }, [requestedUsers]);

  useEffect(() => {
    setIsLoading(true);
    const previousLocation = localStorage.getItem("previousLocation");

    if (previousLocation === "/user/requests/view-profile/:id") {
      dispatch(getUserRequests(reqRequests));
    }

    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    localStorage.setItem("requestActivePage", activePage.toString());
  }, [activePage]);

  useEffect(() => {
    if (clickFilterStatus || clickFilterSearch || paginationChanged) {
      setIsLoading(true);
      debouncedDispatch(getUserRequests, reqRequests);
    }

    return () => {
      debouncedDispatch.cancel();
    };
  }, [
    reqRequests,
    clickFilterStatus,
    clickFilterSearch,
    paginationChanged,
    debouncedDispatch,
  ]);

  const handleChangeSelector = (selected: any) => {
    localStorage.setItem("selectedOptionRequest", JSON.stringify(selected));
    setSelectedOption(selected);
    handleSetFilterByStatus(selected.label);
  };

  const handleSetFilterByStatus = (data: any) => {
    setClickFilterStatus(true);
    setClickFilterSearch(false);
    setPaginationChanged(false);
    setActivePage(1);
    updateSettings({
      PageNumber: 1,
      ParameterThree: data === "All" ? "Requested,Rejected" : data,
    });
  };

  const handleSetFilteredValue = (inputValue: string) => {
    setClickFilterStatus(false);
    setClickFilterSearch(true);
    setPaginationChanged(false);
    setActivePage(1);
    updateSettings({
      PageNumber: 1,
      SearchText: inputValue || "",
    });
  };

  const handlePageChange = (page: number) => {
    setPaginationChanged(true);
    setIsLoading(true);
    setActivePage(page);
    updateSettings({ PageNumber: page });
  };

  return (
    <Container fluid className="layout-bar">
      <BreadCrumb />
      <Headline name="Manage requests"/>

      <div className="content-wrapper">
        <div className="filter-wrapper">
          <FilterSection
            userStatusData={requestedUserStatusData}
            handleSetFilteredValue={handleSetFilteredValue}
            handleSetFilterByStatus={handleSetFilterByStatus}
            selectedOption={selectedOption}
            handleChangeSelector={handleChangeSelector}
          />
        </div>
      </div>

      <div className="box-container">
        <div className="user-management">
          {isLoading ? (
            <Loader />
          ) : requestedUserData?.data?.length! > 0 ? (
            requestedUserData?.data.map((user: any, key: number) => (
              <UserCard user={user.user} key={key} id={user.id} />
            ))
          ) : (
            <SearchHelper
              userdata={requestedUserData}
              isLoading={isLoading}
              filteredValue={[]}
              status={status}
            />
          )}
        </div>
      </div>

      {requestedUserData?.totalPages! > 1 && (
        <Pagination
          pageNumber={activePage}
          onPageChange={handlePageChange}
          totalPages={requestedUserData?.totalPages!}
          pageSize={reqRequests.PageSize}
          totalRecords={requestedUserData?.totalRecords || 0}
          label="Users"
        />
      )}
    </Container>
  );
};

export default UserRequests;
